import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  EnrolleeInfo,
  EnrollmentFormDenormalized,
  EnrollmentFormSubmissionStatus,
  FormFieldSubmission,
  IEnrollmentForm,
  IEnrollmentFormSubmission,
  SubmissionResponseStatus,
  SubmitFormResponse
} from '@dadohr/shared/domainTypes/enrollmentForms';
import {
  ExperienceAnchorType,
  IExperienceTemplate
} from '@dadohr/shared/domainTypes/experience';
import { DADO_TRY_OUT_ORG_ID } from '@dadohr/shared/domainTypes/org';
import { css } from '@emotion/css';
import styled from '@emotion/styled';
import {
  ButtonBase,
  Chip,
  Dialog,
  IconButton,
  Typography
} from '@material-ui/core';
import { Check, Info, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { EnrollmentForm } from '../../../../../01-standalone-app/features/Experience/pages/EnrollmentForms';
import {
  RowsRenderer,
  ROW_HEIGHTS,
  useSortQueryParam
} from '../../../../../components/GroupableList';
import { Loader } from '../../../../../components/Loader';
import { TableSearch } from '../../../../../components/Table';
import { useSnackbar } from '../../../../../hooks/useSnackbar';
import { useUnsafeTranslation } from '../../../../../i18next';
import { FlexContainer } from '../../../../../layout/Flex';
import { useStringQueryParam } from '../../../../../routes';
import { useCurrentUser } from '../../../../../services/currentUser';
import {
  toEnrollmentFormDenormalized,
  useEnrollSubmissionById
} from '../../../../../services/experiences/enrollmentForms';
import { updateDoc } from '../../../../../services/firebase';
import { now } from '../../../../../services/time';
import { toSearchTermRegexp } from '../../../../../services/user';
import { useTheme } from '../../../../../themes';
import {
  D,
  getSubmissionFormFullName,
  O,
  useEnrollmentFormSubmissionsColumnsAndSorters
} from './Columns';

const ROW_TO_KEY = (d: D) => d.id;

const Container = styled('div')((p) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: p.theme.spacing(1),
  paddingLeft: p.theme.spacing(2),
  paddingRight: p.theme.spacing(2)
}));

const FormContainer = styled('div')((p) => ({
  padding: p.theme.spacing(2)
}));

const InfoContainer = styled('div')((p) => ({
  display: 'flex',
  gap: p.theme.spacing(1),
  padding: `${p.theme.spacing(2)}px ${p.theme.spacing(3)}px`,
  backgroundColor: p.theme.custom.colors['gray'].w100,
  borderBottom: p.theme.custom.border.standard
}));

const FormDialog = ({
  open,
  onClose,
  enrollmentFormDenormalized,
  selectedFormSubmission
}: {
  open: boolean;
  onClose: () => void;
  enrollmentFormDenormalized: EnrollmentFormDenormalized;
  selectedFormSubmission: Doc<IEnrollmentFormSubmission>;
}) => {
  const { t } = useUnsafeTranslation();
  const { enqueueSnackbarSuccess, enqueueSnackbarError } = useSnackbar();
  const isReadOnly =
    selectedFormSubmission.data.status ===
      EnrollmentFormSubmissionStatus.COMPLETE ||
    selectedFormSubmission.data.status ===
      EnrollmentFormSubmissionStatus.REJECTED;

  const saveFormSubmissionChanges = async (
    submissionData: FormFieldSubmission[],
    formSubmission: Doc<IEnrollmentFormSubmission>
  ): Promise<SubmitFormResponse> => {
    const submissionUpdate: Partial<IEnrollmentFormSubmission> = {
      data: submissionData,
      modifiedAt: now()
    };

    try {
      await updateDoc(formSubmission, (_) => submissionUpdate);
      enqueueSnackbarSuccess(t('Changes Saved'));
    } catch (err: any) {
      console.error(err);
      enqueueSnackbarError('Failed to save changes! Please try again later!');
    }

    const enrolleeInfo: EnrolleeInfo = {
      fullName: '',
      email: ''
    };
    return {
      status: SubmissionResponseStatus.SUCCESS,
      enrolleeInfo,
      isTryOutDadoOrg: selectedFormSubmission.data.orgId === DADO_TRY_OUT_ORG_ID
    };
  };
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <FormContainer>
        <FlexContainer fullWidth justifyContent="flex-end" marginBottom={2}>
          <IconButton onClick={onClose}>
            <X size={20} />
          </IconButton>
        </FlexContainer>
        <EnrollmentForm
          enrollmentForm={enrollmentFormDenormalized}
          initialFormSubmission={selectedFormSubmission}
          onSubmit={(submissionsData) =>
            saveFormSubmissionChanges(submissionsData, selectedFormSubmission)
          }
          isReadOnly={isReadOnly}
          onCancel={onClose}
          noBorder
          skipSuccessScreen
          submitButtonLabel={t('Update')}
        />
      </FormContainer>
    </Dialog>
  );
};

const EmptyState = () => {
  const { t } = useUnsafeTranslation();
  return (
    <FlexContainer marginTop={8} justifyContent="center">
      <Typography variant="caption">{t('No submissions found')}</Typography>
    </FlexContainer>
  );
};

export const Submissions = ({
  enrollmentFormDoc,
  tpl
}: {
  enrollmentFormDoc: Doc<IEnrollmentForm>;
  tpl: Doc<IExperienceTemplate>;
}) => {
  const enrollmentForm = enrollmentFormDoc.data;
  const { org } = useCurrentUser();
  const theme = useTheme();
  const { t } = useUnsafeTranslation();
  const [filtered, setFiltered] = useState<Set<string>>(new Set());
  const [q, setQ] = useStringQueryParam('q', '');
  const [
    selectedFormSubmission,
    setSelectedFormSubmission
  ] = useState<Doc<IEnrollmentFormSubmission> | null>(null);

  const [
    enrollmentFormDenormalized,
    setEnrollmentFormDenormalized
  ] = useState<EnrollmentFormDenormalized>();

  useEffect(() => {
    if (enrollmentForm && org) {
      toEnrollmentFormDenormalized(enrollmentForm, tpl.data, org).then((e) => {
        setEnrollmentFormDenormalized(e);
      });
    }
  }, [enrollmentForm, tpl, org]);

  const [rows, loading, error] = useEnrollSubmissionById(
    enrollmentForm.id,
    org.id
  );
  const {
    columns,
    sorters,
    defaultSorter
  } = useEnrollmentFormSubmissionsColumnsAndSorters(enrollmentForm, tpl);
  const [[sorter, dir], setSort] = useSortQueryParam('sort', sorters);

  const otherProps: O = useMemo(() => {
    return {
      enrollmentForm,
      orgDateFormat: org.dateFormats.MMDDYYYY
    };
  }, [enrollmentForm, org.dateFormats.MMDDYYYY]);

  const filterOptions: {
    key: EnrollmentFormSubmissionStatus;
    label: string;
    color: string;
    backgroundColor: string;
  }[] = useMemo(
    () => [
      {
        key: EnrollmentFormSubmissionStatus.COMPLETE,
        label: t('Enrolled'),
        color: theme.custom.colors.green.w600,
        backgroundColor: theme.custom.colors.green.w100
      },
      {
        key: EnrollmentFormSubmissionStatus.PENDING,
        label: t('Pending'),
        color: theme.custom.colors.yellow.w800,
        backgroundColor: theme.custom.colors.yellow.w200
      },
      {
        key: EnrollmentFormSubmissionStatus.REJECTED,
        label: t('Rejected'),
        color: theme.custom.colors.red.w600,
        backgroundColor: theme.custom.colors.red.w100
      },
      {
        key: EnrollmentFormSubmissionStatus.ERROR,
        label: t('Error'),
        color: theme.custom.colors.red.w600,
        backgroundColor: theme.custom.colors.red.w100
      },
      {
        key: EnrollmentFormSubmissionStatus.REJECTED_FOR_WRONG_EMAIL_DOMAIN,
        label: t('Invalid'),
        color: theme.custom.colors.red.w600,
        backgroundColor: theme.custom.colors.red.w100
      }
    ],
    [t, theme.custom.colors]
  );

  const filteredRows = useMemo(() => {
    if (!rows) {
      return [];
    }
    if (!filtered.size && !q) {
      return rows;
    }
    const matchFormSubmissionBySearchTerm = (
      regexp: RegExp,
      f: Doc<IEnrollmentFormSubmission>
    ) => {
      return regexp.exec(getSubmissionFormFullName(f, enrollmentForm));
    };

    let fItems = rows.filter((a) => !filtered.has(a.data.status));
    const regexp = toSearchTermRegexp(q);

    return fItems.filter((f) => matchFormSubmissionBySearchTerm(regexp, f));
  }, [rows, filtered, q, enrollmentForm]);

  if (loading) {
    return <Loader height={500} />;
  }

  if (error) {
    return null; //TODO: make error state more descriptive
  }

  return (
    <>
      {tpl.data.anchor.type !== ExperienceAnchorType.EMPLOYEE_DATE && (
        <InfoContainer>
          <Info size={20} />
          <Typography>
            {t(
              "The Participant's experience will start immediately after you click Enroll"
            )}
          </Typography>
        </InfoContainer>
      )}
      <Container>
        <FlexContainer marginTop={2} justifyContent="space-between">
          <FlexContainer>
            {filterOptions.map((a) => (
              <ChipWithAction
                key={a.key}
                label={a.label}
                color={a.color}
                backgroundColor={a.backgroundColor}
                icon={
                  !filtered.has(a.key) ? (
                    <Check size={16} color={a.color} />
                  ) : undefined
                }
                onClick={() =>
                  setFiltered((filter) => {
                    const nextFilter = new Set([...filter]);
                    if (nextFilter.has(a.key)) {
                      nextFilter.delete(a.key);
                    } else {
                      nextFilter.add(a.key);
                    }
                    return nextFilter;
                  })
                }
              />
            ))}
          </FlexContainer>
          <TableSearch
            term={q}
            onChange={setQ}
            autoFocus
            placeholder={t('users.overview.search')}
          />
        </FlexContainer>
        {filteredRows.length ? (
          <RowsRenderer
            variant="contained"
            columns={columns}
            rows={filteredRows}
            rowToKey={ROW_TO_KEY}
            sorter={sorter || defaultSorter}
            sortDirection={dir}
            renderHead={true}
            headProps={{ sticky: true }}
            onHeadClick={(c, d) =>
              setSort([sorters[c.key] || defaultSorter, d])
            }
            onRowClick={(d) => setSelectedFormSubmission(d)}
            chunkSize={30}
            rootMargin="300px"
            rowHeight={ROW_HEIGHTS.dense}
            otherProps={otherProps}
          />
        ) : (
          <EmptyState />
        )}
        {selectedFormSubmission && enrollmentFormDenormalized && (
          <FormDialog
            open={true}
            onClose={() => setSelectedFormSubmission(null)}
            enrollmentFormDenormalized={enrollmentFormDenormalized}
            selectedFormSubmission={selectedFormSubmission}
          />
        )}
      </Container>
    </>
  );
};

//DUPLICATED COMPONENT AND WILL BE REMOVED ONCE OTHER TICKETS ARE MERGED INTO MASTER

const ChipWithAction = ({
  label,
  color,
  backgroundColor,
  icon,
  onClick
}: {
  label: string;
  color: string;
  backgroundColor: string;
  icon?: React.ReactElement;
  onClick: () => void;
}) => {
  const theme = useTheme();
  const chipStyle = {
    root: css({
      color: `${color} !important`,
      backgroundColor: `${backgroundColor} !important`,
      fontWeight: theme.typography.fontWeightMedium,
      ':hover': {
        cursor: 'pointer'
      }
    })
  };

  return (
    <ButtonBase onClick={onClick}>
      <Chip
        label={
          <FlexContainer justifyContent="center">
            <Typography variant="caption" color="inherit">
              {label}
            </Typography>
            {icon}
          </FlexContainer>
        }
        size="small"
        classes={chipStyle}
      />
    </ButtonBase>
  );
};
